import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Stack, styled,
} from "@mui/material";
import { CustomButton } from "@/components/custom/custom-button";
import { CustomErrorMessage } from "@/components/custom/custom-error-message";
import { FormTextInput } from "@/components/custom/custom-form-text-input";
import { FormTextArea } from "@/components/custom/custom-form-textarea";
import { showToast } from "@/redux/actions/toast-actions";
import { smsGatewaySchema } from "@/schemas/notification.schema";
import { useCreateSmsGateway, useUpdateSmsGateway } from "@/hooks/use-notifications";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: theme.spacing(2),
    maxWidth: 460,
    width: "100%",
  },
}));

const GatewayFormDialog = ({ open, gateway, onClose }) => {
  const dispatch = useDispatch();
  const isEdit = Boolean(gateway);
  const createMutation = useCreateSmsGateway();
  const updateMutation = useUpdateSmsGateway();
  const mutation = isEdit ? updateMutation : createMutation;

  const formik = useFormik({
    initialValues: {
      name: gateway?.name ?? "",
      description: gateway?.description ?? "",
    },
    enableReinitialize: true,
    validationSchema: smsGatewaySchema,
    validateOnMount: true,
    onSubmit: (values) => {
      const payload = { name: values.name.trim(), description: values.description.trim() };
      const run = isEdit
        ? updateMutation.mutateAsync({ id: gateway.id, ...payload })
        : createMutation.mutateAsync(payload);

      run
        .then((res) => {
          dispatch(showToast(res?.message ?? (isEdit ? "Gateway updated" : "Gateway added"), "success"));
          onClose();
        })
        .catch((err) => dispatch(showToast(err?.message ?? "Failed to save gateway", "error")));
    },
  });

  useEffect(() => {
    if (open) mutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, gateway]);

  const handleClose = () => {
    if (mutation.isPending) return;
    formik.resetForm();
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
        <DialogTitle sx={{ p: 0, fontSize: "1.1rem", fontWeight: 600 }}>
          {isEdit ? "Edit SMS Gateway" : "Add SMS Gateway"}
        </DialogTitle>

        {mutation.isError && (
          <CustomErrorMessage message={mutation.error?.message ?? "Failed to save gateway"} />
        )}

        <Stack spacing={2}>
          <FormTextInput
            formik={formik}
            name="name"
            label="Gateway name"
            placeholder="e.g. Termii"
            isDisabled={mutation.isPending}
          />
          <FormTextArea
            formik={formik}
            name="description"
            label="Description"
            placeholder="What this gateway is used for..."
            rows={3}
            isDisabled={mutation.isPending}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2, pt: 1, pb: 1, gap: 1 }}>
        <CustomButton variantType="outline" size="small" onClick={handleClose} disabled={mutation.isPending} sx={{ px: 2 }}>
          Cancel
        </CustomButton>
        <CustomButton
          size="small"
          disabled={!formik.isValid || mutation.isPending}
          isLoading={mutation.isPending}
          onClick={formik.handleSubmit}
          sx={{ px: 2 }}
        >
          {mutation.isPending ? "Saving..." : isEdit ? "Save Changes" : "Add Gateway"}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default GatewayFormDialog;